import { LoadingMaskService } from './base/services/loading-mask.service';
import { SystemMessageService } from './base/services/system-message.service';

import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { LanguageService } from './base/services/language.service';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { delay, filter, Subject, takeUntil } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { PrimeNGConfig } from 'primeng/api';
import { DomHandler } from 'primeng/dom';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

  isIframe: boolean = false;
  loginDisplay: boolean = false;
  showLoadingMask: boolean = false;

  private readonly _destroying$ = new Subject<void>();

  constructor(private primengConfig: PrimeNGConfig,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private languageService: LanguageService,
    private title: Title,
    private translateService: TranslateService,
    private loadingMaskService: LoadingMaskService,
    private router: Router,
    private systemMessageService: SystemMessageService) {
    // 注入 languageService 並執行初始化語系
    this.languageService.setInitState();
  }

  ngOnInit() {
    // 清除所有 localStorage 儲存資料
    localStorage.clear();

    this.primengConfig.ripple = true;

    this.isIframe = window !== window.parent && !window.opener;

    // 訂閱 LOGIN_SUCCESS 事件，這可讓您從使用重新導向的成功登入存取結果
    // 只有在第一次登入成功的時候會做
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      )
      .subscribe((result: EventMessage) => {
        console.log('msalBroadcastService LOGIN_SUCCESS: ' + result);
      });

    // 訂閱 MSAL MsalBroadcastService inProgress$ 可觀察事件
    // 使用此事件可知道使用者互動的狀態，檢查使用者是否通過驗證。
    // 換頁面都會跑
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        // 這是官網範例寫的，實際上專案沒用到這個 setLoginDisplay
        this.setLoginDisplay();
        console.log('msalBroadcastService loginDisplay: ' + this.loginDisplay);
      });

    this.translateService.onLangChange.pipe(
        takeUntil(this._destroying$)
      ).subscribe((event: LangChangeEvent) => {
        this.title.setTitle(this.translateService.instant('system-title'));
      });

    this.loadingMaskService.status$
      .pipe(
        delay(1),
        takeUntil(this._destroying$)
      )
      .subscribe((val: boolean) => {
        this.showLoadingMask = val;
      });

    // 當 router 換頁後清除所有顯示的訊息
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._destroying$)
      )
      .subscribe(
        () => this.systemMessageService.clear()
      );

  }

  // 這是官網範例寫的，實際上專案沒用到這個 loginDisplay
  // 官網範例是用來判斷是否已經登入
  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  /**
   * 在第一個 Dialog 未關閉的狀況下，裡面如果出現提示訊息，或是又開另一個 Dialog，又關閉會出現另一條 scrollbar，
   * 這個是把第二條 scrollbar 取消
   */
  confirmDialogHide(): void {
    // 第一層是為了解決開啟 Dialog 後，按下刪除，會直接把 Dialog 關閉，這時候馬上去偵測 'p-dynamicdialog' 其實還在
    // 如果這時候把 scrollbar 取消，回到前面畫面就不能捲了
    setTimeout(() => {
      if (DomHandler.findSingle(document.body, 'p-dynamicdialog')) {
        let id = setInterval(() => {
          if (!DomHandler.hasClass(document.body, 'p-overflow-hidden')) {
            DomHandler.addClass(document.body, 'p-overflow-hidden');
          }
        }, 50);

        setTimeout(() => {
          clearInterval(id);
        }, 1000);
      }
    }, 1000);
  }

  ngOnDestroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
